// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/utils/oauthApi.js
//
// Thin wrapper around the OAuth endpoints the admin frontend uses.
//
// Three calls:
//
//   listConnections({ appName, region, scope })
//     GET /oauth/connections?app_name=<app>&scope=<app|tenant>
//     Returns { connections: [...], providers: [...] } on success.
//     `appName` is optional when `scope="tenant"` (Global Context).
//
//   revokeConnection({ appName, region, provider, externalAccountId, scope })
//     DELETE /oauth/revoke?provider=...&external_account_id=...&app_name=...&scope=...
//     Returns { success: true, provider_revoked: <bool>, ... } on success.
//     `appName` is required in both scopes — the backend needs it to
//     resolve the exact row when both an app-scoped and a tenant-scoped
//     row exist for the same external account.
//
//   startOAuthFlow({ appName, region, provider, scope, redirectAfter })
//     GET /oauth/start?... with a bearer header. Returns the provider's
//     authorize URL as a string. The caller is responsible for
//     navigating the browser to it.
//     `appName` is optional when `scope="tenant"` (Global Context).
//
// Every function throws on network errors and on non-2xx responses,
// with the parsed server error message when one is available. Callers
// catch and surface via their own UI (in this admin, ReusablePopup).

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

function buildApiBase(region) {
  const regionLower = (region || "us").toLowerCase();
  return `https://${regionLower}.connector-handler2.${backend_api_base_url}`;
}

function getToken() {
  return localStorage.getItem("token") || "";
}

/**
 * Read the current connections + available providers.
 *
 * `scope` may be "app" (default) or "tenant". When "tenant", `appName`
 * may be omitted — the backend returns tenant-scoped rows only.
 */
export async function listConnections({ appName, region, scope = "app" }) {
  if (scope === "app" && !appName) {
    throw new Error("Missing appName for scope='app'");
  }

  const params = new URLSearchParams();
  if (appName) params.set("app_name", appName);
  if (scope) params.set("scope", scope);

  const url = `${buildApiBase(region)}/oauth/connections?${params.toString()}`;

  const resp = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: getToken(),
    },
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {
    // non-JSON body — handled below
  }

  if (!resp.ok || !body?.success) {
    const msg =
      body?.error ||
      body?.detail ||
      body?.message ||
      `Failed to load connections (HTTP ${resp.status})`;
    throw new Error(msg);
  }

  return {
    connections: Array.isArray(body.connections) ? body.connections : [],
    providers: Array.isArray(body.providers) ? body.providers : [],
  };
}

/**
 * Disconnect an existing connection.
 *
 * `appName` and `scope` are both required because the backend needs
 * them to resolve the exact row when both an app-scoped row and a
 * tenant-scoped row exist for the same external account.
 */
export async function revokeConnection({
  appName,
  region,
  provider,
  externalAccountId,
  scope = "app",
}) {
  if (!appName) throw new Error("Missing appName");
  if (!provider) throw new Error("Missing provider");
  if (!externalAccountId) throw new Error("Missing externalAccountId");

  const params = new URLSearchParams({
    provider,
    external_account_id: externalAccountId,
    app_name: appName,
    scope,
  });

  const url = `${buildApiBase(region)}/oauth/revoke?${params.toString()}`;

  const resp = await fetch(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: getToken(),
    },
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {
    // non-JSON body — handled below
  }

  if (!resp.ok || !body?.success) {
    const msg =
      body?.error ||
      body?.detail ||
      body?.message ||
      `Failed to disconnect (HTTP ${resp.status})`;
    throw new Error(msg);
  }

  return body;
}

/**
 * Initiate an OAuth flow.
 *
 * `scope` may be "app" (default) or "tenant". When "tenant", `appName`
 * may be omitted — the flow is initiated at the tenant level.
 */
export async function startOAuthFlow({
  appName,
  region,
  provider,
  scope = "app",
  redirectAfter,
}) {
  if (scope === "app" && !appName) {
    throw new Error("Missing appName for scope='app'");
  }
  if (!provider) throw new Error("Missing provider");

  const params = new URLSearchParams({ provider, scope });
  if (appName) params.set("app_name", appName);

  // Fall back to the current admin origin so the user lands back
  // where they started. The backend validates this against an
  // allowlist; if the origin isn't allowlisted, the call fails at
  // /oauth/start with a 400 and the modal surfaces it.
  const fallbackRedirect =
    redirectAfter ||
    (typeof window !== "undefined" ? window.location.origin : "");
  if (fallbackRedirect) {
    params.set("redirect_after", fallbackRedirect);
  }

  const url = `${buildApiBase(region)}/oauth/start?${params.toString()}`;

  const resp = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: getToken(),
    },
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {
    // non-JSON body — handled below
  }

  if (!resp.ok || !body?.success || !body?.authorize_url) {
    const msg =
      body?.error ||
      body?.detail ||
      body?.message ||
      `Failed to start the connection (HTTP ${resp.status})`;
    throw new Error(msg);
  }

  return body.authorize_url;
}